import { useAlertDialog } from '@/components/common/alert-dialog-provider';
import { ListItem } from '@/components/common/list-item';
import { ResponsiveContainer } from '@/components/layout/responsive-container';
import { TemplateListSkeleton } from '@/components/skeletons/template-list-skeleton';
import { Button } from '@/components/ui/button';
import { convexQuery, useConvexMutation } from '@convex-dev/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Id } from 'convex/_generated/dataModel';
import { Calendar, Copy, Globe, Lock, Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '~/convex/_generated/api';
import type { Template } from '~/convex/schema';

export const TemplatesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: templates } = useQuery(convexQuery(api.templates.list, {}));
  const { data: currentUser } = useQuery(convexQuery(api.user.currentUser, {}));

  // Organize templates into personal and public
  const { personalTemplates, publicTemplates } = useMemo(() => {
    if (!templates) {
      return { personalTemplates: [], publicTemplates: [] };
    }

    const personal = templates.filter(
      (template) => template.ownerId === currentUser?._id,
    );
    const public_ = templates.filter((template) => template.isPublic);

    return {
      personalTemplates: personal,
      publicTemplates: public_,
    };
  }, [templates, currentUser?._id]);

  return (
    <>
      {/* Header */}
      <ResponsiveContainer
        scrollAwareBorder
        className="flex items-center justify-between bg-white py-4 md:pt-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('templates:title')}
        </h1>
        <Button onClick={() => navigate('/templates/create')} size="sm">
          <Plus className="size-4" />
          {t('templates:create')}
        </Button>
      </ResponsiveContainer>

      {/* Content */}
      <ResponsiveContainer className="relative min-h-0 flex-1 grow overflow-y-auto pt-2">
        {templates === undefined ? (
          <TemplateListSkeleton />
        ) : personalTemplates.length === 0 && publicTemplates.length === 0 ? (
          <TemplatesListEmpty />
        ) : (
          <>
            {/* Personal Templates Section */}
            {personalTemplates.length > 0 && (
              <>
                <h2 className="pt-2 pb-2 text-sm font-semibold text-gray-700">
                  {t('templates:personalTemplates')}
                </h2>
                <div className="flex flex-col gap-3 pb-4">
                  {personalTemplates.map((template) => (
                    <TemplateItem
                      key={template._id}
                      template={template}
                      canEdit
                    />
                  ))}
                </div>
              </>
            )}

            {/* Public Templates Section */}
            {publicTemplates.length > 0 && (
              <>
                <h2 className="pt-2 pb-2 text-sm font-semibold text-gray-700">
                  {t('templates:publicTemplates')}
                </h2>
                <div className="flex flex-col gap-3 md:pb-4">
                  {publicTemplates.map((template) => (
                    <TemplateItem
                      key={template._id}
                      template={template}
                      canEdit={false}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </ResponsiveContainer>
    </>
  );
};

const TemplateItem = ({
  template,
  canEdit = false,
}: {
  template: Template;
  canEdit?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const alertDialog = useAlertDialog();

  const { mutate: deleteTemplate, isPending: isDeleting } = useMutation({
    mutationFn: useConvexMutation(api.templates.remove),
  });

  const { mutate: duplicateTemplate, isPending: isDuplicating } = useMutation({
    mutationFn: useConvexMutation(api.templates.duplicate),
  });

  const handleDelete = async (id: Id<'templates'>) => {
    const confirmed = await alertDialog.confirm({
      title: t('templates:deleteConfirmTitle'),
      description: t('templates:deleteConfirmDescription', {
        name: template.name,
      }),
      confirmText: t('templates:delete'),
      cancelText: t('templates:cancel'),
      variant: 'destructive',
    });

    if (confirmed) {
      deleteTemplate(
        { id },
        {
          onError: (error) => {
            toast.error(t('errors:errorDeletingTemplate'));
            console.error('Error deleting template:', error);
          },
        },
      );
    }
  };

  const handleDuplicate = (id: Id<'templates'>) => {
    duplicateTemplate(
      { id },
      {
        onSuccess: () => {
          toast.success(t('templates:templateDuplicated'));
        },
        onError: (error) => {
          toast.error(t('errors:errorDuplicatingTemplate'));
          console.error('Error duplicating template:', error);
        },
      },
    );
  };

  const createdAt = new Date(template._creationTime).toLocaleDateString(
    i18n.language,
  );

  return (
    <ListItem
      title={template.name}
      trailingTitle={template.language}
      description={
        <>
          {template.isPublic ? (
            <>
              <Globe className="relative -top-0.25 mr-1 inline size-3.5 text-gray-500" />
              {t('templates:public')}
            </>
          ) : (
            <>
              <Lock className="relative -top-0.25 mr-1 inline size-3.5 text-gray-500" />
              {t('templates:private')}
            </>
          )}
          {t('templates:separator')}
          <Calendar className="relative -top-0.25 mr-1 inline size-3.5 text-gray-500" />
          {createdAt}
          {t('templates:separator')}
          {t('templates:section', { count: template.sections.length })}
        </>
      }
      actions={
        <div className="flex gap-1">
          {/* Duplicate button - available for all templates */}
          <Button
            variant="ghost"
            size="icon"
            disabled={isDuplicating}
            loading={isDuplicating}
            onClick={(event) => {
              event.stopPropagation();
              handleDuplicate(template._id);
            }}>
            <Copy className="size-4 text-gray-600" />
          </Button>
          {/* Delete button - only for owned templates */}
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="hover:bg-red-100/80"
              disabled={isDeleting}
              loading={isDeleting}
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(template._id);
              }}>
              <Trash2 className="size-4 text-red-600" />
            </Button>
          )}
        </div>
      }
      onClick={() => {
        // Navigate to edit page only if the user can edit
        if (canEdit) {
          navigate(`/templates/${template._id}`);
        } else {
          navigate(`/templates/${template._id}/view`);
        }
      }}
    />
  );
};

const TemplatesListEmpty = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <>
      <TemplateListSkeleton pulse={false} />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
        <p className="text-sm font-medium text-gray-900">
          {t('templates:noTemplates')}
        </p>
        <p className="mb-2 text-sm text-gray-500">
          {t('templates:noTemplatesDescription')}
        </p>
        <Button variant="outline" onClick={() => navigate('/templates/create')}>
          <Plus className="size-4" />
          {t('templates:create')}
        </Button>
      </div>
    </>
  );
};
